import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, X, Loader2, ArrowRight, Car as CarIcon, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { FORMAT } from "@/lib/site";

export default function GlobalSearch({ open, onClose }) {
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ cars: [], pages: [] });
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef(null);
    const navigate = useNavigate();

    // Reset when opened
    useEffect(() => {
        if (open) {
            setQ("");
            setData({ cars: [], pages: [] });
            setActiveIndex(0);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open]);

    // Debounced search
    useEffect(() => {
        if (!open) return;
        const query = q.trim();
        if (query.length < 2) {
            setData({ cars: [], pages: [] });
            setLoading(false);
            return;
        }
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const r = await api.get("/search", { params: { q: query } });
                setData(r.data);
                setActiveIndex(0);
            } catch (_) {
                setData({ cars: [], pages: [] });
            } finally {
                setLoading(false);
            }
        }, 220);
        return () => clearTimeout(t);
    }, [q, open]);

    // Flat list of results for keyboard nav
    const flat = [...data.pages.map((p) => ({ ...p, kind: "page" })), ...data.cars.map((c) => ({ ...c, kind: "car" }))];

    const go = (item) => {
        if (!item) return;
        navigate(item.path);
        onClose();
    };

    const onKeyDown = (e) => {
        if (e.key === "Escape") return onClose();
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, Math.max(0, flat.length - 1)));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => Math.max(0, i - 1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            go(flat[activeIndex]);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    className="fixed inset-0 z-[100] flex items-start justify-center pt-24 px-4 bg-ink/40 backdrop-blur-sm"
                    onClick={onClose}
                    data-testid="global-search-overlay"
                >
                    <motion.div
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -12, opacity: 0 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                        className="w-full max-w-2xl bg-surface border border-line rounded-3xl shadow-soft overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="global-search-modal"
                    >
                        {/* Input */}
                        <div className="flex items-center gap-3 px-6 py-5 border-b border-line">
                            {loading ? <Loader2 size={18} className="text-gold animate-spin" /> : <Search size={18} className="text-ink-muted" />}
                            <input
                                ref={inputRef}
                                type="text"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                onKeyDown={onKeyDown}
                                placeholder="Търсете автомобили, услуги, страници..."
                                data-testid="global-search-input"
                                className="flex-1 bg-transparent text-ink placeholder:text-ink-subtle focus:outline-none text-base"
                            />
                            <button
                                onClick={onClose}
                                className="p-1.5 rounded-full hover:bg-bg text-ink-muted"
                                aria-label="Затвори"
                                data-testid="global-search-close"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Results */}
                        <div className="max-h-[60vh] overflow-y-auto">
                            {q.trim().length < 2 ? (
                                <div className="px-6 py-10 text-center text-ink-muted text-sm">
                                    Напишете поне 2 символа за търсене
                                    <div className="mt-3 flex flex-wrap gap-2 justify-center">
                                        {["Mercedes", "Диагностика", "Финансиране", "Отзиви"].map((s) => (
                                            <button
                                                key={s}
                                                onClick={() => setQ(s)}
                                                className="text-xs px-3 py-1 rounded-full border border-line hover:border-gold/40 hover:text-gold"
                                            >
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : flat.length === 0 && !loading ? (
                                <div className="px-6 py-10 text-center text-ink-muted text-sm" data-testid="global-search-empty">
                                    Няма резултати за „<span className="text-ink">{q}</span>"
                                </div>
                            ) : (
                                <div className="py-2">
                                    {data.pages.length > 0 && (
                                        <>
                                            <div className="px-6 pt-3 pb-1 text-[10px] uppercase tracking-[0.3em] text-ink-subtle">Страници</div>
                                            {data.pages.map((p, i) => {
                                                const idx = i;
                                                const active = idx === activeIndex;
                                                return (
                                                    <button
                                                        key={p.path}
                                                        onMouseEnter={() => setActiveIndex(idx)}
                                                        onClick={() => go({ ...p, kind: "page" })}
                                                        data-testid={`result-page-${p.path.replace(/\//g, "-")}`}
                                                        className={`w-full flex items-center gap-3 px-6 py-3 text-left transition-colors ${active ? "bg-bg" : "hover:bg-bg"}`}
                                                    >
                                                        <span className="w-9 h-9 rounded-lg bg-gold/10 text-gold border border-gold/20 flex items-center justify-center shrink-0">
                                                            <FileText size={15} />
                                                        </span>
                                                        <span className="flex-1 min-w-0">
                                                            <span className="block text-sm text-ink truncate">{p.title}</span>
                                                            <span className="block text-xs text-ink-muted truncate">{p.desc}</span>
                                                        </span>
                                                        <ArrowRight size={14} className="text-ink-subtle shrink-0" />
                                                    </button>
                                                );
                                            })}
                                        </>
                                    )}
                                    {data.cars.length > 0 && (
                                        <>
                                            <div className="px-6 pt-4 pb-1 text-[10px] uppercase tracking-[0.3em] text-ink-subtle">Автомобили</div>
                                            {data.cars.map((c, i) => {
                                                const idx = data.pages.length + i;
                                                const active = idx === activeIndex;
                                                return (
                                                    <button
                                                        key={c.id}
                                                        onMouseEnter={() => setActiveIndex(idx)}
                                                        onClick={() => go({ ...c, kind: "car" })}
                                                        data-testid={`result-car-${c.id}`}
                                                        className={`w-full flex items-center gap-3 px-6 py-3 text-left transition-colors ${active ? "bg-bg" : "hover:bg-bg"}`}
                                                    >
                                                        {c.image ? (
                                                            <img src={c.image} alt="" className="w-12 h-9 rounded object-cover shrink-0" />
                                                        ) : (
                                                            <span className="w-12 h-9 rounded bg-gold/10 text-gold flex items-center justify-center shrink-0">
                                                                <CarIcon size={16} />
                                                            </span>
                                                        )}
                                                        <span className="flex-1 min-w-0">
                                                            <span className="block text-sm text-ink truncate">{c.title}</span>
                                                            <span className="block text-xs text-ink-muted truncate">{c.subtitle}</span>
                                                        </span>
                                                        {c.price != null && (
                                                            <span className="text-sm text-gold font-medium shrink-0">
                                                                {FORMAT?.money ? FORMAT.money(c.price) : `${c.price} €`}
                                                            </span>
                                                        )}
                                                        <ArrowRight size={14} className="text-ink-subtle shrink-0" />
                                                    </button>
                                                );
                                            })}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer hint */}
                        <div className="px-6 py-3 border-t border-line text-[11px] text-ink-subtle flex items-center justify-between">
                            <span>↑↓ навигация · Enter отвори · Esc затвори</span>
                            <span className="hidden sm:inline">⌘K за бърз достъп</span>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
